/**
 * Straight product detail — variant picker, gallery, wishlist.
 * Call StraightPdp.init(root) after the .straight-pdp element is in the DOM.
 */
(function () {
  function readWishlist() {
    const WISH_KEY = 'straight:wishlist';
    try {
      const raw = localStorage.getItem(WISH_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const obj = {};
        parsed.forEach((item) => {
          if (item && item.handle) obj[item.handle] = item;
        });
        return obj;
      }
      return parsed;
    } catch (e) {
      return {};
    }
  }

  function writeWishlist(data) {
    const WISH_KEY = 'straight:wishlist';
    try {
      localStorage.setItem(WISH_KEY, JSON.stringify(data));
    } catch (e) {
      /* storage full or disabled */
    }
    document.dispatchEvent(new CustomEvent('straight:wishlist:change', { detail: { wishlist: data } }));
  }

  function syncWishButton(btn, wishlist) {
    const handle = btn.getAttribute('data-fav-handle');
    if (!handle) return;
    btn.setAttribute('aria-pressed', wishlist[handle] ? 'true' : 'false');
  }

  function init(root) {
    if (!root || root.dataset.straightPdpBound === 'true') return;
    root.dataset.straightPdpBound = 'true';

    const sectionId = root.getAttribute('data-section-id');
    const container = root.closest('.shopify-section, .straight-pdp-section, [data-straight-pdp-content]') || root.parentElement || root;
    function readPdpData() {
      const sel = '[data-pdp-variants-' + sectionId + ']';
      const dataNode =
        root.querySelector(sel) ||
        container.querySelector(sel) ||
        document.querySelector(sel);
      if (!dataNode) return { variants: [], color_index: -1, size_index: -1 };
      try {
        const raw = (dataNode.value != null ? dataNode.value : dataNode.textContent) || '';
        return JSON.parse(raw.trim());
      } catch (e) {
        return { variants: [], color_index: -1, size_index: -1 };
      }
    }

    let data = readPdpData();

    const swatches = root.querySelectorAll('[data-pdp-swatch]');
    const sizePills = root.querySelectorAll('[data-pdp-size-pill]');
    const idInput = root.querySelector('[data-pdp-variant-id]');
    const priceEl = root.querySelector('[data-pdp-price]');
    const atcBtn = root.querySelector('[data-pdp-atc]');
    const atcLabel = root.querySelector('[data-pdp-atc-label]');
    const galleryImg = root.querySelector('.straight-pdp__gallery-image');
    const galleryWrap = root.querySelector('[data-pdp-gallery]');
    let galleryThumbs = root.querySelectorAll('[data-pdp-thumb]');
    let selectedGalleryIndex = 0;

    function normalizeImageUrl(url) {
      if (!url) return '';
      try {
        return new URL(url, window.location.origin).pathname;
      } catch (e) {
        return String(url).split('?')[0];
      }
    }

    function findThumbIndexForImage(url, imageId) {
      let index = -1;
      galleryThumbs.forEach((thumb, i) => {
        if (index >= 0) return;
        const thumbId = thumb.getAttribute('data-image-id') || '';
        const thumbPath = normalizeImageUrl(thumb.getAttribute('data-image-url'));
        if (imageId && thumbId === String(imageId)) index = i;
        else if (url && thumbPath === normalizeImageUrl(url)) index = i;
      });
      return index >= 0 ? index : 0;
    }

    function syncActiveThumb(index) {
      if (!galleryThumbs.length) return;
      if (typeof index !== 'number' || index < 0 || index >= galleryThumbs.length) index = 0;
      selectedGalleryIndex = index;

      galleryThumbs.forEach((thumb, i) => {
        const isActive = i === selectedGalleryIndex;
        thumb.classList.toggle('is-active', isActive);
        if (isActive) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    function swapGalleryImage(url, imageId, index) {
      if (typeof index === 'number') selectedGalleryIndex = index;
      else if (url || imageId) selectedGalleryIndex = findThumbIndexForImage(url, imageId);

      if (!galleryImg || !url) {
        syncActiveThumb(selectedGalleryIndex);
        return;
      }

      if (normalizeImageUrl(galleryImg.src) === normalizeImageUrl(url)) {
        syncActiveThumb(selectedGalleryIndex);
        return;
      }

      const finish = () => {
        galleryImg.src = url;
        galleryImg.removeAttribute('srcset');
        galleryImg.removeAttribute('sizes');
        syncActiveThumb(selectedGalleryIndex);
        window.requestAnimationFrame(() => {
          if (galleryWrap) galleryWrap.classList.remove('is-swapping');
        });
      };

      if (!galleryWrap) {
        finish();
        return;
      }

      galleryWrap.classList.add('is-swapping');
      window.setTimeout(finish, 240);
    }

    function pulsePrice() {
      if (!priceEl) return;
      priceEl.classList.remove('is-updating');
      void priceEl.offsetWidth;
      priceEl.classList.add('is-updating');
    }

    let currentColor = null;
    let currentSize = null;

    if (data.color_index >= 0) {
      const selected = root.querySelector('[data-pdp-swatch].is-selected');
      currentColor = selected ? selected.getAttribute('data-color-value') : null;
    }
    const selectedPill = root.querySelector('[data-pdp-size-pill].is-selected');
    if (selectedPill) currentSize = selectedPill.getAttribute('data-size-value');
    else if (sizePills.length) {
      const mPill = Array.from(sizePills).find((pill) => {
        const size = pill.getAttribute('data-size-value');
        return size && size.trim().toUpperCase() === 'M';
      });
      currentSize = (mPill || sizePills[0]).getAttribute('data-size-value');
    }

    function findVariant() {
      return data.variants.find((v) => {
        if (data.color_index >= 0 && v.options[data.color_index] !== currentColor) return false;
        if (data.size_index >= 0 && v.options[data.size_index] !== currentSize) return false;
        return true;
      });
    }

    function setUnavailable(label) {
      if (idInput) idInput.value = '';
      if (atcLabel) atcLabel.textContent = label;
      if (atcBtn) {
        atcBtn.setAttribute('disabled', 'disabled');
        atcBtn.classList.add('is-sold-out');
      }
    }

    function applyVariant() {
      const v = findVariant();

      if (!v) {
        if (!data.variants.length) return;
        setUnavailable('Unavailable');
        return;
      }

      if (idInput) idInput.value = v.id;
      if (priceEl && priceEl.textContent !== v.price_formatted) {
        priceEl.textContent = v.price_formatted;
        pulsePrice();
      } else if (priceEl) {
        priceEl.textContent = v.price_formatted;
      }

      if (v.image && galleryImg) {
        swapGalleryImage(v.image, v.image_id, findThumbIndexForImage(v.image, v.image_id));
      } else if (galleryImg) {
        syncActiveThumb(selectedGalleryIndex);
      }

      if (!v.available) {
        setUnavailable('Sold out');
        return;
      }

      if (atcLabel) atcLabel.textContent = 'Add to Bag';
      if (atcBtn) {
        atcBtn.removeAttribute('disabled');
        atcBtn.classList.remove('is-sold-out');
      }
    }

    function refreshSizeAvailability() {
      if (data.size_index < 0) return;
      sizePills.forEach((pill) => {
        const size = pill.getAttribute('data-size-value');
        const match = data.variants.find((v) => {
          if (data.color_index >= 0 && v.options[data.color_index] !== currentColor) return false;
          return v.options[data.size_index] === size;
        });
        const available = !!(match && match.available);
        if (available) pill.removeAttribute('disabled');
        else pill.setAttribute('disabled', 'disabled');
      });
    }

    swatches.forEach((btn) => {
      btn.addEventListener('click', () => {
        swatches.forEach((b) => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-checked', 'true');
        currentColor = btn.getAttribute('data-color-value');
        refreshSizeAvailability();
        applyVariant();
      });
    });

    sizePills.forEach((pill) => {
      pill.addEventListener('click', () => {
        if (pill.hasAttribute('disabled')) return;
        const value = pill.getAttribute('data-size-value');
        currentSize = value;
        sizePills.forEach((p) => {
          p.classList.remove('is-selected');
          p.setAttribute('aria-selected', 'false');
        });
        pill.classList.add('is-selected');
        pill.setAttribute('aria-selected', 'true');
        applyVariant();
      });
    });

    function bindGalleryThumbs() {
      galleryThumbs.forEach((thumb) => {
        if (thumb.dataset.pdpThumbBound === 'true') return;
        thumb.dataset.pdpThumbBound = 'true';
        thumb.addEventListener('click', () => {
          const url = thumb.getAttribute('data-image-url');
          const imageId = thumb.getAttribute('data-image-id');
          const index = Array.prototype.indexOf.call(galleryThumbs, thumb);
          if (!galleryImg) return;
          if (url) swapGalleryImage(url, imageId, index);
          else syncActiveThumb(index);
        });
      });
    }

    bindGalleryThumbs();

    galleryThumbs.forEach((thumb, i) => {
      if (thumb.classList.contains('is-active')) selectedGalleryIndex = i;
    });

    refreshSizeAvailability();
    applyVariant();

    root.querySelectorAll('[data-pdp-wishlist], [data-fav-toggle]').forEach((btn) => {
      const handle = btn.getAttribute('data-fav-handle');
      if (!handle) return;
      const sync = () => syncWishButton(btn, readWishlist());
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const wishlist = readWishlist();
        if (wishlist[handle]) {
          delete wishlist[handle];
        } else {
          wishlist[handle] = {
            id: btn.getAttribute('data-fav-id'),
            title: btn.getAttribute('data-fav-title') || '',
            url: btn.getAttribute('data-fav-url') || '',
            addedAt: Date.now(),
          };
        }
        writeWishlist(wishlist);
        document.querySelectorAll('[data-fav-handle="' + handle + '"]').forEach((b) => syncWishButton(b, wishlist));
      });
      document.addEventListener('straight:wishlist:change', sync);
      window.addEventListener('storage', (e) => {
        if (e.key === 'straight:wishlist') sync();
      });
      sync();
    });
  }

  window.StraightPdp = { init };
})();
